import numbro from 'numbro';

export function formatMoneyCompact(amount: number, currencyCode: string) {
  return new Intl.NumberFormat([], {
    style: 'currency',
    currency: currencyCode,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);
}

export function formatMoneyRounded(amount: number | string, currencyCode: string) {
  const moneyFormatter = new Intl.NumberFormat([], {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  });

  return moneyFormatter.format(amount as number);
}

export function formatMoney(amount: number | string, currencyCode: string) {
  try {
    const moneyFormatter = new Intl.NumberFormat([], {
      style: 'currency',
      currency: currencyCode,
    });

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/format#number
    return moneyFormatter.format(amount as number);
  } catch {
    // Fall back gracefully if the currency code isn't a valid ISO code.
    return `${Number(amount).toFixed(2)} ${currencyCode}`;
  }
}

// Formats a number to the correct number of decimal places for that currency
export function formatMoneyNoSymbol(amount: number, currencyCode: string): string {
  const info = new Intl.NumberFormat([], {
    style: 'currency',
    currency: currencyCode,
  }).resolvedOptions();

  const formatter = new Intl.NumberFormat([], {
    minimumFractionDigits: info.minimumFractionDigits,
    maximumFractionDigits: info.maximumFractionDigits,
  });

  return formatter.format(amount);
}

// Gets the symbol for the currency
export function getCurrencySymbol(currencyCode: string): string {
  const formatter = new Intl.NumberFormat([], {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(0).replace(/\d/g, '').trim();
}

/**
 * Source: https://stackoverflow.com/questions/29255843/is-there-a-way-to-reverse-the-formatting-by-intl-numberformat-in-javascript
 * Parse a localized number to a float.
 * @param stringNumber - the localized number
 * @param locale - the locale that the number is represented in. Omit this
 *    parameter to use the current locale.
 */
export function parseLocaleNumber(stringNumber: string, locale?: string): number {
  const thousandSeparator = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, '');
  const decimalSeparator = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, '');

  return parseFloat(
    stringNumber
      .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
      .replace(new RegExp(`\\${decimalSeparator}`), '.'),
  );
}

/**
 * Convert a string representing price a to a Number
 *
 * Handles European-formatted numbers which use a comma for the decimal separator
 * Thousand separators are not allowed
 *
 * @throws When priceStr is not a string
 * @throws When more than one separator character is found
 */
export function priceStringAsNumber(priceStr: string): number {
  if (typeof priceStr !== 'string') {
    throw Error('Price must be a string');
  }

  const normalisedPrice = numbro.unformat(priceStr);
  if (normalisedPrice === undefined) {
    throw Error(`Unable to parse price "${priceStr}"`);
  }

  return normalisedPrice;
}
